import {
  type Gender,
  type ObjectType,
  PersonIdentityCode,
} from '@quromedical/fhir-common';
import { last, sum } from '@quromedical/utils';
import { isValid, parse } from 'date-fns';
import * as yup from 'yup';

import {
  citySchema,
  countrySchema,
  linesSchema,
  postalCodeSchema,
} from './address';
import type {
  PersonContact,
  PersonContactWithAddressEmergencyAccess,
  PersonGeneral,
} from './schema';

const toInt = (v: string) => +v;

const isValidDateOfBirth = (idNumber: string): boolean => {
  const year = idNumber.slice(0, 2);
  const month = toInt(idNumber.slice(2, 4));
  const day = toInt(idNumber.slice(4, 6));

  const date = `${year}-${month}-${day}`;
  const parsedDate = parse(date, 'yy-MM-dd', new Date());
  const isValidDate = isValid(parsedDate);

  return isValidDate;
};

/**
 * From https://en.wikipedia.org/wiki/National_identification_number#South_Africa
 * @param input
 * @return true if the value is valid
 */
export const isValidSouthAfricanIdNumber = (input = ''): boolean => {
  const trimmed = input.trim();

  /** According to luhn's algorithm '0000000000000' is a valid ID number. But ID numbers have more
   * rules besides luhn's algorithm. */
  if (!isValidDateOfBirth(trimmed)) {
    return false;
  }
  const chars = trimmed.split('');

  const numericRegex = /^\d+$/;
  const isNumeric = numericRegex.test(trimmed);

  if (!isNumeric) {
    return false;
  }

  if (chars.length !== 13) {
    return false;
  }

  const checksum = +chars[chars.length - 1];

  const odds = chars
    .slice(0, -1)
    .filter((_, i) => i % 2 === 0)
    .map(toInt);

  const evens = chars.filter((_, i) => i % 2 === 1).map(toInt);

  const A = sum(odds);
  const B = evens.join('');
  const C = sum((2 * +B).toString().split('').map(toInt));
  const D = A + C;
  const Z = 10 - (D % 10);

  /** If the answer from Z is one digit, that should be the same as the checksum.
   * If it’s more than one digit, the last digit of the answer from Z should the
   * same as the checksum. Therefor you can always check the last digit of Z and
   * it should equal the checksum */
  const checkString = last(Z.toString().split(''));

  if (!checkString) {
    return false;
  }

  const checkDigit = toInt(checkString);

  return checkDigit === checksum;
};

export const getGenderFromSouthAfricanIdNumber = (input = '') => {
  const isIdValid = isValidSouthAfricanIdNumber(input);
  if (!isIdValid) {
    return undefined;
  }
  const genderIndex = input.trim().split('')[6];
  const gender: Gender = toInt(genderIndex) >= 5 ? 'male' : 'female';

  return gender;
};

export const getBirthDateFromSouthAfricanIdNumber = (input = '') => {
  const isIdValid = isValidSouthAfricanIdNumber(input);
  if (!isIdValid) {
    return undefined;
  }
  const trimmed = input.trim().slice(0, 6);
  const relative = new Date();
  const birthDate = parse(trimmed, 'yyMMdd', relative);

  return birthDate;
};

export const phoneRegex = /^\+[0-9]?[0-9]{0,16}$/;

export const phoneSchema = yup.string().required().matches(phoneRegex, {
  message: 'Use an international phone number format. e.g. +276451230429',
});
export const telecomPhoneNumbersSchema = yup
  .array(phoneSchema.required())
  .required()
  .min(1);
export const emailSchema = yup.string().lowercase().email().required();

export const saIdNumber = yup
  .string()
  .required()
  .test({
    name: 'idNumberValidation',
    test: (i) => isValidSouthAfricanIdNumber(i || ''),
    message: 'SA ID number is not valid',
  })
  .length(13);

export const namibianIdNumber = yup.string().required().length(11);

export const usernameSchema = yup.string().test({
  name: 'AuthUsername',
  test: (username) => {
    const isEmail = emailSchema.isValidSync(username);
    if (isEmail) {
      return true;
    }

    return phoneSchema.isValidSync(username);
  },
});

const telecomPhoneNumbersSchemas = yup
  .mixed<string[] | undefined>()
  .test({
    test: (lines = []) => lines[0]?.length > 0,
  })
  .test({
    test: (lines = []) => {
      const line = lines.filter((v) => v !== undefined);
      return telecomPhoneNumbersSchema.isValidSync(line);
    },
  });

/**
 * This is a schema for a person's contact details. It is used in the patient and other persons.
 */
export const personContactFieldSchemas: Partial<
  Record<keyof PersonContactWithAddressEmergencyAccess, yup.AnySchema>
> = {
  addressLines: linesSchema,
  addressCity: citySchema,
  addressPostalCode: postalCodeSchema,
  addressCountry: countrySchema,
  telecomEmail: emailSchema,
  // using an object like below due to the way the fields are configured in formik
  telecomPhoneNumbers: telecomPhoneNumbersSchemas,
  addressEmergencyAccess: yup.string().optional(),
  addressPlaceId: yup.string().optional(),
};

export const personContactSchema = yup
  .object<ObjectType<PersonContact>>(personContactFieldSchemas)
  .required();

const genders: Gender[] = ['unknown', 'female', 'male', 'other'];
const identifierCodes = Object.values(PersonIdentityCode) as string[];

export const identifierCodeSchema = yup
  .string()
  .required()
  .oneOf(identifierCodes);

interface IdentifierPartial {
  identifierCode?: string;
  identifierValue?: string;
}

export const identifierPartialRequiredFieldSchemas: Partial<
  Record<keyof IdentifierPartial, yup.AnySchema>
> = {
  identifierCode: identifierCodeSchema,
  identifierValue: yup
    .string()
    .required()
    .when('identifierCode', {
      is: PersonIdentityCode.saIdNumber,
      then: saIdNumber,
      otherwise: yup.string().when('identifierCode', {
        is: PersonIdentityCode.namibiaIdNumber,
        then: namibianIdNumber,
        otherwise: yup.string().required(),
      }),
    }),
};

export const personGeneralFieldSchemas: Partial<
  Record<keyof PersonGeneral, yup.AnySchema>
> = {
  prefixName: yup.string().required(),
  givenName: yup.string().required(),
  familyName: yup.string().required(),
  gender: yup.string().required().oneOf(genders),
  birthDate: yup.date().required(),
  ...identifierPartialRequiredFieldSchemas,
};

export const personMinimalGeneralFieldSchema = yup
  .object<ObjectType<PersonGeneral>>({
    prefixName: personGeneralFieldSchemas.prefixName,
    givenName: personGeneralFieldSchemas.givenName,
    familyName: personGeneralFieldSchemas.familyName,
    gender: personGeneralFieldSchemas.gender,
  })
  .defined();

export const personGeneralSchema = yup
  .object<ObjectType<PersonGeneral>>(personGeneralFieldSchemas)
  .required();
